import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import type { RolodexDocumentEntry } from 'features/files'
import { useDocumentData } from 'features/libra/document/DocumentDataContext'
import { useEffect, useMemo, useState } from 'react'

const EXCLUDED_CATEGORIES: ReadonlySet<string> = new Set([
  'zone_reference_images',
])

const UNPROCESSABLE_EXTENSIONS: ReadonlySet<string> = new Set(['xlsx', 'csv'])

function isUnprocessableDocument(doc: RolodexDocumentEntry): boolean {
  const type = doc.file_type?.toLowerCase()
  if (type && UNPROCESSABLE_EXTENSIONS.has(type)) return true
  const ext = doc.filename?.toLowerCase().split('.').pop()
  return !!ext && UNPROCESSABLE_EXTENSIONS.has(ext)
}

type ProcessingStats = Record<string, Record<string, number>>

/**
 * Checks whether all report entries (excluding categories in
 * `EXCLUDED_CATEGORIES` and file types not supported by the processing
 * pipeline) linked to this ticket's unit have been fully processed by the
 * cantilever-ai document processing pipeline.
 *
 * Returns `allProcessed: true` only when every relevant entry has zero
 * pending (`None`) and zero `failed` counts — i.e. fully processed.
 * Gracefully degrades to `allProcessed: true` on API errors so the button
 * is never permanently stuck.
 */
export function useDocumentProcessingReady(): {
  allProcessed: boolean
  isCheckingProcessing: boolean
  unprocessedCount: number
} {
  const { documentsByCategory, isLoading: docsLoading } = useDocumentData()
  const { authorizedAxios: axios } = useGeckoAuth()
  const { ROLODEX_URL } = useRuntimeVariables()

  const [stats, setStats] = useState<ProcessingStats | null>(null)
  const [fetchDone, setFetchDone] = useState(false)

  const relevantEntryIds = useMemo(
    () =>
      [...documentsByCategory.entries()]
        .filter(([category]) => !EXCLUDED_CATEGORIES.has(category))
        .flatMap(([, docs]) =>
          docs.filter((d) => !isUnprocessableDocument(d)).map((d) => d.entry_id)
        ),
    [documentsByCategory]
  )

  useEffect(() => {
    if (docsLoading || relevantEntryIds.length === 0) {
      setFetchDone(!docsLoading)
      setStats(null)
      return
    }

    let cancelled = false
    setFetchDone(false)

    axios
      .post(
        `${ROLODEX_URL}/api/v2/cantilever-ai/document-processing/stats/query`,
        { entry_ids: relevantEntryIds }
      )
      .then((response) => {
        if (!cancelled) {
          setStats(response.data as ProcessingStats)
          setFetchDone(true)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStats(null)
          setFetchDone(true)
        }
      })

    return () => {
      cancelled = true
    }
  }, [relevantEntryIds, docsLoading, axios, ROLODEX_URL])

  return useMemo(() => {
    if (relevantEntryIds.length === 0) {
      return {
        allProcessed: true,
        isCheckingProcessing: false,
        unprocessedCount: 0,
      }
    }
    if (!fetchDone) {
      return {
        allProcessed: false,
        isCheckingProcessing: true,
        unprocessedCount: 0,
      }
    }
    if (!stats) {
      return {
        allProcessed: true,
        isCheckingProcessing: false,
        unprocessedCount: 0,
      }
    }

    let unprocessed = 0
    for (const entryId of relevantEntryIds) {
      const entryStat = stats[entryId]
      const pending = entryStat?.['None'] ?? 0
      const failed = entryStat?.failed ?? 0
      if (!entryStat || pending > 0 || failed > 0) {
        unprocessed++
      }
    }

    return {
      allProcessed: unprocessed === 0,
      isCheckingProcessing: false,
      unprocessedCount: unprocessed,
    }
  }, [relevantEntryIds, stats, fetchDone])
}
